/**
 * Helper: Product images
 * -----------------------------------------------------------------------------
 * Loads additional product images which aren't available in the product data.
 * - Algolia hits only contain a single image (`image`/`product_image`) so the
 *   rollover (secondary) image has to be requested separately.
 * - Requests are cached in memory and session storage so each product is only
 *   requested once per session.
 * - Requests are queued and made when the browser is idle so they don't compete
 *   with the primary product images.
 *
 */
const SESSION_STORAGE_KEY = 'cnvs:secondary-images'
const MAX_CONCURRENT_REQUESTS = 4

/**
 * Cache of in-flight and resolved requests, keyed by product handle.
 */
const requestCache = new Map()

/**
 * Requests waiting for a free slot.
 */
const requestQueue = []

let activeRequests = 0
let sessionCache = null

/**
 * Get product's secondary (rollover) image.
 * @param {String} handle - Product handle.
 * @returns {Promise<Object|Null>}
 */
export function getSecondaryProductImage(handle) {
  if (!handle) {
    return Promise.resolve(null)
  }

  if (requestCache.has(handle)) {
    return requestCache.get(handle)
  }

  const cachedImage = getSessionCache()[handle]

  if (typeof cachedImage !== 'undefined') {
    const cachedRequest = Promise.resolve(cachedImage)
    requestCache.set(handle, cachedRequest)
    return cachedRequest
  }

  const request = queueRequest(handle)
  requestCache.set(handle, request)

  return request
}

/**
 * Queue product request.
 * - Limits concurrent requests so they don't delay other assets.
 * @param {String} handle - Product handle.
 * @returns {Promise<Object|Null>}
 */
function queueRequest(handle) {
  return new Promise((resolve) => {
    requestQueue.push({ handle, resolve })
    processQueue()
  })
}

/**
 * Process queued requests up to the concurrency limit.
 */
function processQueue() {
  if (activeRequests >= MAX_CONCURRENT_REQUESTS || !requestQueue.length) {
    return
  }

  const { handle, resolve } = requestQueue.shift()
  activeRequests += 1

  whenIdle(async() => {
    const image = await requestProductImage(handle)

    setSessionCache(handle, image)
    resolve(image)

    activeRequests -= 1
    processQueue()
  })
}

/**
 * Run callback when the browser is idle.
 * @param {Function} callback - Callback to run.
 */
function whenIdle(callback) {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(callback, { timeout: 1000 })
    return
  }

  setTimeout(callback, 0)
}

/**
 * Request product JSON and format its secondary image.
 * @param {String} handle - Product handle.
 * @returns {Promise<Object|Null>}
 */
async function requestProductImage(handle) {
  try {
    const url = `${cnvs.routes.normalisedRoot}products/${encodeURIComponent(handle)}.js`
    const response = await fetch(url, { priority: 'low' })

    if (!response.ok) {
      return null
    }

    const product = await response.json()

    return formatSecondaryImage(product)

  } catch {
    return null
  }
}

/**
 * Format second image of product JSON response.
 * - Matches the object shape expected by `responsive-image`.
 * @param {Object} product - Product JSON (`/products/{handle}.js`) response.
 * @returns {Object|Null}
 */
function formatSecondaryImage(product) {
  const media = Array.isArray(product?.media)
    ? product.media.filter((item) => item.media_type === 'image')
    : []

  const image = media[1]

  if (image) {
    return {
      alt: image.alt || product.title || '',
      aspectRatio: image.width && image.height
        ? image.width / image.height
        : null,
      height: image.height || null,
      id: image.id,
      index: 1,
      src: image.src || image.preview_image?.src || null,
      width: image.width || null,
    }
  }

  const fallbackSrc = Array.isArray(product?.images) ? product.images[1] : false

  if (!fallbackSrc) {
    return null
  }

  return {
    alt: product.title || '',
    aspectRatio: null,
    height: null,
    id: null,
    index: 1,
    src: fallbackSrc,
    width: null,
  }
}

/**
 * Get session storage cache.
 * @returns {Object}
 */
function getSessionCache() {
  if (sessionCache) {
    return sessionCache
  }

  try {
    sessionCache = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEY)) || {}
  } catch {
    sessionCache = {}
  }

  return sessionCache
}

/**
 * Save image data to the session storage cache.
 * @param {String} handle - Product handle.
 * @param {Object|Null} image - Formatted image data.
 */
function setSessionCache(handle, image) {
  const cache = getSessionCache()
  cache[handle] = image

  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(cache))
  } catch {
    // Silently fail if session storage is full or unavailable
  }
}
